import { useState } from "react";
import { Check, Edit2, Loader2, X, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface DocumentHeaderProps {
  document: DocumentWithMetadata;
  onClose: () => void;
  onDocumentUpdate?: (document: DocumentWithMetadata, updates: Partial<DocumentWithMetadata>) => Promise<any>;
  isEditing: boolean;
  onToggleEdit: () => void;
  readOnly?: boolean;
}

export const DocumentHeader = ({ document, onClose, onDocumentUpdate, isEditing, onToggleEdit, readOnly }: DocumentHeaderProps) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [documentName, setDocumentName] = useState(document.name);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveChanges = async () => {
    if (!document?.id || !onDocumentUpdate) return;

    setIsSaving(true);
    try {
      await onDocumentUpdate(document, { name: documentName });
      setIsEditingName(false);
    } catch (error) {
      console.error("Error updating document name:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDocumentName(document.name);
    setIsEditingName(false);
  };

  return (
    <div className="relative">
      {/* Gradient top bar */}
      <div className="h-2 bg-gradient-to-r from-genie-primary to-genie-secondary" />

      <div className="flex justify-between items-center gap-4 border-b-2 border-gray-100 pb-5 pt-6 px-6">
        {/* min-w-0 so a long document name truncates instead of pushing the
            Edit/Close buttons off the edge of the drawer. */}
        <div className="flex min-w-0 items-center gap-4">
          <h2 className="min-w-0 text-2xl font-bold text-gray-900">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <Input
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  className="text-2xl font-bold border-2 border-genie-primary/30 focus:border-genie-primary p-2 h-auto min-w-[400px] rounded-lg"
                  placeholder="Document name"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  title="Save changes"
                  className="h-9 w-9 hover:bg-genie-primary/10 text-genie-primary rounded-lg"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  title="Cancel editing"
                  className="h-9 w-9 hover:bg-genie-text/5 text-genie-text/60 rounded-lg"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate" title={document.name}>{document.name}</span>
                {isEditing && !readOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsEditingName(true)}
                    title="Edit document title"
                    className="h-9 w-9 hover:bg-genie-primary/10 text-genie-primary rounded-lg transition-all hover:scale-105"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </h2>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {!readOnly && (
            <Button
              variant={isEditing ? "default" : "outline"}
              size="sm"
              onClick={onToggleEdit}
              className={
                isEditing
                  ? "bg-gradient-to-r from-genie-primary to-genie-secondary text-white border-0 hover:opacity-90"
                  : "gap-1.5"
              }
            >
              {!isEditing && <Edit2 className="h-4 w-4" />}
              {isEditing ? "Done" : "Edit"}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 rounded-full hover:bg-genie-text/5 text-genie-text/60 hover:text-genie-text transition-all hover:scale-110"
            aria-label="Close document details"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
